import type { CSSProperties } from 'react';
import type { DecorationSpec } from '../types';
import { spriteCount, spriteStyle } from './positions';
import { spriteGlyph } from './sprites';

// v2 §D1: render a `DecorationSpec` as N positioned, animated emoji
// sprites. Pure CSS animation (keyframes in index.css under
// `.decor-motion-*`); each sprite gets its own `top`/`left`/`duration`/
// `delay` from `spriteStyle()` so the same sprite count produces a
// stable, scattered layout across renders.
//
// The wrapper carries `.theme-decoration` (shared with v1 decorations)
// so reduced-motion + fixed-position rules in index.css apply uniformly.
interface DecorationLayerProps {
  spec: DecorationSpec;
}

export function DecorationLayer({ spec }: DecorationLayerProps) {
  const count = spriteCount(spec.density);
  const glyph = spriteGlyph(spec.sprite);
  const motionClass = `decor-motion-${spec.motion}`;
  const spriteClass = `decor-sprite-${spec.sprite}`;

  return (
    <div className={`theme-decoration decor-layer ${motionClass}`} aria-hidden="true">
      {Array.from({ length: count }, (_, i) => {
        const pos = spriteStyle(i, spec);
        const style: CSSProperties = {
          ...pos,
          // Coloured glow when the spec supplies a palette. Index by
          // sprite-index modulo so a 2-colour palette alternates cleanly.
          ...(spec.colors && spec.colors.length > 0
            ? { filter: `drop-shadow(0 0 6px ${spec.colors[i % spec.colors.length]})` }
            : null),
        };
        return (
          <span key={i} className={`decor-sprite ${spriteClass}`} style={style}>
            {glyph}
          </span>
        );
      })}
    </div>
  );
}
